import axios from '@api';
import { normalizeTopicIds } from '@helpers';
import { transformObjectToCamelCase } from '@utilities';
import {
  transformCreateNarrativeResponse,
  transformDraftTopicsData,
  transformFlow,
  transformNarrativeItemsData,
  transformNarrativePrompts,
  transformNarrativeTopics,
  transformNarrativeType,
  transformTopic,
} from './utilities/helpers';

/**
 * read flow
 * @param  {Number} options.id
 * @param  {String} options.publicToken
 * @param  {AbortSignal}  options.signal
 * @return {Promise}
 */
export const readFlow = async ({ id, publicToken, signal }) => {
  // bypass sending cookies when using publicToken
  const withCredentials = publicToken ? false : true;
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/api/flows/${id}`,
    withCredentials,
  }).then(({ data: flow }) => transformFlow(flow));
};

/**
 * ready flow topic
 * @param  {Object} options
 * @param  {Number} options.id
 * @param  {String} options.publicToken
 * @param  {AbortSignal} [options.signal] AbortController signal
 * @return {Promise}
 */
export const readTopic = async ({ id, publicToken, signal }) => {
  const withCredentials = publicToken ? false : true;
  return axios({
    method: 'get',
    params: {
      publicToken,
    },
    signal,
    url: `/api/flows/flowtopics/${id}`,
    withCredentials,
  }).then(({ data: topic }) => transformTopic(topic));
};

/**
 * read draft topic visualizations
 * @param {Object} options
 * @param {Array<string>} options.componentIds
 * @param {AbortSignal} [options.signal]
 * @return {Promise<Object>}
 */
export const readDraftTopics = async ({ componentIds = [], signal } = {}) => {
  if (!Array.isArray(componentIds) || componentIds.length === 0) return {};

  const params = new URLSearchParams();
  componentIds.forEach((componentId) => {
    if (componentId) {
      params.append('topic_id', componentId);
    }
  });

  const queryString = params.toString();
  const url = queryString
    ? `/api/flows/flowtopics/drafts/?${queryString}`
    : '/api/flows/flowtopics/drafts/';

  return axios({
    method: 'get',
    signal,
    url,
    withCredentials: true,
  }).then(({ data }) => transformDraftTopicsData(data));
};

/**
 * read narrative list V2
 * @param  {Number} options.folderId
 * @param  {String} options.order   "asc" || "desc" || undefined
 * @param  {Number} options.page
 * @param  {String} options.query
 * @param  {Number} options.signal  AbortController signal
 * @param  {Number} options.size
 * @param  {String} options.sortBy
 * @param  {Array}  options.tags    [is_shared]
 * @return {Promise}
 */
export const readNarrativeItems = ({ order, page, query, signal, size, sortBy, tags }) => {
  return axios({
    method: 'get',
    params: {
      order_by: sortBy ? order : undefined,
      page,
      query: query?.length > 0 ? query : undefined,
      size,
      sort_by: sortBy,
      tags: tags?.length > 0 ? tags?.join(',') : undefined,
    },
    signal,
    transformResponse: [(data) => transformNarrativeItemsData(JSON.parse(data))],
    url: '/v2/api/flows',
    withCredentials: true,
  });
};

/**
 * update narrative name
 * @param  {Number} options.id
 * @param  {String} options.name
 * @return {Promise}
 */
export const updateNarrativeName = ({ id, name }) => {
  const trimmedName = name?.trim();
  if (!name || !trimmedName)
    return Promise.reject(new Error('New narrative name cannot be blank.'));
  return axios({
    data: { name: trimmedName },
    method: 'put',
    url: `/v2/api/flows/${id}/rename`,
    withCredentials: true,
  });
};

/**
 * reads flow share token for read only
 * @param  {Number}  options.id
 * @return {Promise}
 */
export const readFlowShareToken = ({ id }) => {
  return axios({
    method: 'put',
    transformResponse: [
      (data) => {
        const response = JSON.parse(data);
        return response?.publicToken;
      },
    ],
    url: `/api/flows/${id}/share`,
    withCredentials: true,
  });
};

/**
 * reads flow public token and retrieves flow id
 * @param  {String} options.publicToken
 * @return {Promise}
 */
export const readFlowPublicToken = ({ publicToken }) => {
  return axios({
    method: 'get',
    transformResponse: [
      (data) => {
        const response = JSON.parse(data);
        return response?.flowId;
      },
    ],
    url: `/api/flows/check-token/${publicToken}`,
    withCredentials: false,
  });
};

/**
 * Update topic visibility
 * @param {Object} options
 * @param {Number} options.id
 * @param {boolean} options.isHidden
 * @return {Promise}
 */
export const updateTopicVisibility = async ({ id, isHidden }) => {
  return axios({
    data: { is_hidden: isHidden },
    method: 'put',
    url: `/v2/api/flows/flowtopics/${id}/hide`,
    withCredentials: true,
  });
};

/**
 * Reorder topics within a completed Narrative run
 * @param {Object} options
 * @param {Number|String} options.flowId
 * @param {Array<Number|String>} options.topicIds ordered list of flow topic ids
 * @return {Promise}
 */
export const reorderFlowTopics = ({ flowId, topicIds = [] }) => {
  if (!flowId) {
    return Promise.reject(new Error('flowId is required to reorder topics.'));
  }

  return axios({
    data: { flow_topic_ids: topicIds },
    method: 'put',
    url: `/v2/api/flows/${flowId}/flowtopics/order`,
    withCredentials: true,
  });
};

/**
 * Permanently delete a flow topic
 * @param {Object} options
 * @param {Number|String} options.topicId flow topic identifier
 * @return {Promise}
 */
export const softDeleteFlowTopic = ({ topicId }) => {
  if (!topicId) {
    return Promise.reject(new Error('topicId is required to delete a topic.'));
  }

  return axios({
    method: 'put',
    url: `/v2/api/flows/flowtopics/${topicId}/soft_delete`,
    withCredentials: true,
  });
};

/**
 * shares narrative with all users under a client
 * @param  {Number} options.clientId
 * @param  {Number} options.ids
 * @return {Promise}
 */
export const shareNarrativeWithClientGroup = ({ clientId, ids }) => {
  return axios({
    data: {
      flow_ids: ids,
      recipient_client_id: clientId,
    },
    method: 'post',
    url: '/v2/api/flows/client_share',
    withCredentials: true,
  });
};

/**
 * shares narrative with users
 * @param  {Array} options.ids
 * @param  {Boolean} options.skipEmail
 * @param  {Number} options.userIds
 * @return {Promise}
 */
export const shareNarrativeWithUsers = ({ ids, skipEmail = false, userIds }) => {
  return axios({
    data: {
      flow_ids: ids,
      recipient_ids: userIds,
      skip_email: skipEmail,
    },
    method: 'post',
    url: '/v2/api/flows/user_share',
    withCredentials: true,
  });
};

/**
 * shares narrative templates with all users under a client
 * @param  {Number} options.clientId
 * @param  {Array<Number|String>} options.ids
 * @return {Promise}
 */
export const shareNarrativeTemplatesWithClientGroup = ({ clientId, ids }) => {
  return axios({
    data: {
      recipient_client_id: clientId,
      template_ids: ids,
    },
    method: 'post',
    url: '/v2/api/narrative-templates/client_share',
    withCredentials: true,
  });
};

/**
 * shares narrative templates with users
 * @param  {Array<Number|String>} options.ids
 * @param  {Boolean} options.skipEmail
 * @param  {Array<Number|String>} options.userIds
 * @return {Promise}
 */
export const shareNarrativeTemplatesWithUsers = ({ ids, skipEmail = false, userIds }) => {
  return axios({
    data: {
      recipient_ids: userIds,
      skip_email: skipEmail,
      template_ids: ids,
    },
    method: 'post',
    url: '/v2/api/narrative-templates/user_share',
    withCredentials: true,
  });
};

/**
 * deletes narratives
 * @param {Array} options.ids
 * @return {Promise}
 */
export const deleteNarratives = ({ ids }) => {
  return axios({
    data: {
      flow_ids: ids,
    },
    method: 'delete',
    url: '/v2/api/flows',
    withCredentials: true,
  });
};

/**
 * update narrative favorite status
 * @param  {Number} options.id
 * @param  {Boolean} options.isFavorite
 * @return {Promise}
 */
export const updateNarrativeFavorite = ({ id, isFavorite }) => {
  return axios({
    data: {
      is_favorite: isFavorite,
    },
    method: 'post',
    url: `/v2/api/flows/${id}/favorite`,
    withCredentials: true,
  });
};

/**
 * returns data about an individual narrative
 * @function
 * @param {Function} transformFunction - Function to transform the fetched data (e.g., transformNarrativeType or transformNarrativePrompts).
 * @returns {Function} - A function that takes an options object with { flowId, narrativeId, publicToken, signal, topicIds }, where publicToken, signal, and topicIds are optional, and returns a Promise resolving to the transformed data.
 */
const readNarrativeTypeCustomFunction = (transformFunction) => {
  return ({ flowId, narrativeId, publicToken, signal, topicIds }) => {
    const withCredentials = publicToken ? false : true;
    const params = new URLSearchParams();

    if (publicToken != null) {
      params.append('publicToken', publicToken);
    }

    const normalizedTopicIds = normalizeTopicIds(topicIds);

    if (normalizedTopicIds.length > 0) {
      normalizedTopicIds.forEach((topicId) => {
        params.append('topic_ids', topicId);
      });
    } else if (flowId != null) {
      params.append('flow_id', flowId);
    }

    const queryString = params.toString();
    const url = queryString
      ? `/api/narratives/${narrativeId}/?${queryString}`
      : `/api/narratives/${narrativeId}/`;

    return axios({
      method: 'get',
      signal,
      url,
      withCredentials,
    }).then(({ data }) => transformFunction(data));
  };
};

/**
 * Reads and transforms All Information about a Narrative type from api
 *
 * @function
 * @param {String} narrativeId i.e. brand-defense
 * @param {Number} flowId i.e. 123
 * @param {String} publicToken
 * @param {AbortSignal} [signal]
 * @param {Array<String>} topicIds optional array of topic ids to fetch for dynamic narratives, used instead of flowId requirement
 * @returns {Promise<Object>} Promise resolving to the transformed Narrative data
 */
export const readNarrativeType = readNarrativeTypeCustomFunction(transformNarrativeType);

/**
 * Reads and transforms Narrative to just it's prompts for use in ItemAnswers component
 *
 * @function
 * @param {String} narrativeId i.e. brand-defense
 * @param {String} publicToken
 * @param {AbortSignal} [signal]
 * @returns {Promise<Object>} Promise resolving to the transformed Narrative prompts data.
 */
export const readNarrativePrompts = readNarrativeTypeCustomFunction(transformNarrativePrompts);

/**
 * @param {Object} options
 * @param {Number} [options.flowId]
 * @param {String} options.id
 * @param {Number|String|null} [options.projectId] optional project identifier sent as `project_id`
 * @param {PromptAnswer[]} options.promptAnswers
 * @param {Array<Number|String>} [options.topicIds]
 * @param {String} options.title
 * @return {Promise}
 */
export const createNarrative = async ({
  flowId,
  id,
  projectId = null,
  promptAnswers,
  title,
  topicIds = [],
}) => {
  const isDynamicNarrative = id === 'dynamic';
  const normalizedTopicIds = normalizeTopicIds(topicIds);

  return axios({
    data: {
      answers: promptAnswers,
      name: title,
      ...(projectId != null ? { project_id: projectId } : {}),
      ...(isDynamicNarrative && flowId != null ? { flow_id: flowId } : {}),
      ...(isDynamicNarrative && normalizedTopicIds.length > 0
        ? { topic_ids: normalizedTopicIds }
        : {}),
    },
    method: 'post',
    url: `/api/narratives/${id}`,
    withCredentials: true,
  }).then(({ data }) => transformCreateNarrativeResponse(data));
};

/**
 * Get all narrative types
 * @return {Promise}
 */
export const readNarratives = () => {
  return axios({
    method: 'get',
    url: '/v2/api/narratives',
    withCredentials: true,
  }).then(({ data }) => data);
};

/**
 * Get narrative templates
 * @return {Promise}
 */
export const readNarrativeTemplates = () => {
  return axios({
    method: 'get',
    url: '/v2/api/narrative-templates',
    withCredentials: true,
  }).then(({ data }) => transformObjectToCamelCase(data));
};

/**
 * Get all narrative topics
 * @return {Promise}
 */
export const readNarrativeTopics = ({ signal, tags = [] } = {}) => {
  const normalizedTags = (Array.isArray(tags) ? tags : [tags]).filter(Boolean);

  return axios({
    method: 'get',
    ...(normalizedTags.length ? { params: { tags: normalizedTags.join(',') } } : {}),
    signal,
    url: '/v2/api/narratives/topics',
    withCredentials: true,
  }).then(({ data }) => transformNarrativeTopics(data));
};

/**
 * Search narrative topics semantically.
 * @param {Object} options
 * @param {AbortSignal} [options.signal]
 * @param {string} options.userInput
 * @return {Promise<Array<string>>}
 */
export const readNarrativeTopicSearch = ({ signal, userInput }) => {
  return axios({
    method: 'get',
    params: {
      user_input: userInput,
    },
    signal,
    url: '/v2/api/narratives/search',
    withCredentials: true,
  }).then(({ data }) => (Array.isArray(data) ? data : []));
};

/**
 * Get all narrative topic tags
 * @return {Promise}
 */
export const readNarrativeTopicsTags = ({ signal } = {}) => {
  return axios({
    method: 'get',
    signal,
    url: '/v2/api/narratives/tags',
    withCredentials: true,
  }).then(({ data }) => data);
};

/**
 * Creates a narrative template.
 * @param {Object} options
 * @param {String} options.name
 * @param {String} options.description
 * @param {Array<String>} options.topicIds
 * @return {Promise}
 */
export const createNarrativeTemplate = ({ description = '', name, topicIds = [] }) => {
  return axios({
    data: {
      description,
      name,
      topic_ids: topicIds,
    },
    method: 'post',
    url: '/v2/api/narrative-templates',
    withCredentials: true,
  });
};

/**
 * Updates a narrative template.
 * @param {Object} options
 * @param {String} options.description
 * @param {String} options.name
 * @param {String|Number} options.templateId
 * @param {Array<String>} options.topicIds
 * @return {Promise}
 */
export const updateNarrativeTemplate = ({ description = '', name, templateId, topicIds = [] }) => {
  if (templateId === null || templateId === undefined) {
    return Promise.reject(new Error('templateId is required to update a narrative template.'));
  }

  return axios({
    data: {
      description,
      name,
      topic_ids: topicIds,
    },
    method: 'put',
    url: `/v2/api/narrative-templates/${templateId}`,
    withCredentials: true,
  });
};

/**
 * Deletes narrative templates.
 * @param {Object} options
 * @param {Array<String|Number>} options.ids
 * @return {Promise}
 */
export const deleteNarrativeTemplates = ({ ids = [] }) => {
  if (!Array.isArray(ids) || ids.length === 0) {
    return Promise.reject(new Error('ids are required to delete narrative templates.'));
  }

  return axios({
    data: ids,
    method: 'delete',
    url: '/v2/api/narrative-templates',
    withCredentials: true,
  });
};
